import {
    Component,
    Input,
    Output,
    Directive,
    QueryList,
    EventEmitter,
    forwardRef,
    ContentChildren,
    AfterContentInit,
    ContentChild,
    ViewChild
} from "@angular/core";
import {
    Pagination, LazyPageData, Restraints, LazyLoadEvent, OrderMeta
} from "../pagination/pagination.component";
import {Header, Footer} from "../common/common";


export class DataTable implements AfterContentInit {

     pageSize: number = 10;
     lazy: boolean = false;
     data: Array<any> | LazyPageData;
     selectionMode: string;
     selection: Array<any> = [];
     availablePageSizes: number[];
     tableClasses: string = "";

     onLazy: EventEmitter<LazyLoadEvent> = new EventEmitter<LazyLoadEvent>();
     rowSelection: EventEmitter<SelectionEvent> = new EventEmitter<SelectionEvent>();
     rowEdit: EventEmitter<EditEvent> = new EventEmitter<EditEvent>();
     private columns: QueryList<Column>;
     private header: Header;
     private footer: Footer;

     private paginator: Pagination;
    private currentSortedColumn: Column;
    
    ngAfterContentInit(): any {
        if (this.isSingleSelectionMode() && this.selection.length > 1) {
            this.selection.splice(1, this.selection.length - 1);
        }
    }

    private filterFunction: Function = (data: Array<any>, restraints: Restraints) => {
        return data.filter(row => {
            let fulfilled: boolean = true;
            for (let key in restraints.filterBy) {
                if (restraints.filterBy.hasOwnProperty(key)) {
                    fulfilled = fulfilled && this.valueOf(row, key).includes(restraints.filterBy[key]);
                }
            }
            return fulfilled;
        });
    };

    private sortFunction = (data: Array<any>, restraints: Restraints) => {
        let orderBy: OrderMeta = restraints.orderBy;
        if (!orderBy) return data;
        data.sort((a, b) => this.alphabeticalCompare(this.valueOf(a, orderBy.field), this.valueOf(b, orderBy.field), orderBy.order));
        return data;
    };
    
    private sort(restraints: Restraints, column: Column): void {
        if (column.sortBy === undefined) return;
        this.currentSortedColumn = column;
        restraints.orderBy = {field: column.content, order: column.order};
        this.paginator.sort();
        column.order = column.order * -1;
    }

    private alphabeticalCompare(value1: any, value2: any, order: number): number {
        let result: number = 0;
        if (value1 instanceof String && value2 instanceof String) {
            result = value1.localeCompare(value2.toString());
        } else {
            result = (value1 < value2) ? -1 : (value1 > value2) ? 1 : 0;
        }
        return result * order;
    }

    private valueOf(root: any, path: string): any {
        let value: any = root;
        path.split(".").forEach(field => {
            value = value[field];
        });
        return value;
    }

    private handleRowClick(row: any): void {
        if (this.isOneClickSelectionMode()) {
            this.select(row);
        }
    }

    private handleRowDblClick(row: any): void {
        if (this.isDoubleClickSelectionMode()) {
            this.select(row);
        }
    }

    private select(row: any): void {
        let selectedRow: any;
        let unselectedRow: any;

        if (this.isSingleSelectionMode()) {
            // TODO Consider cleaning selection array after selection mode change
            if (this.isDoubleClickSelectionMode()) {
                this.selection.splice(0, this.selection.length);
                selectedRow = row;
                unselectedRow = undefined;
            } else {
                unselectedRow = this.selection[0];
                this.selection.splice(0, this.selection.length);
                if (unselectedRow !== row) {
                    this.selection.push(row);
                    selectedRow = row;
                }
            }
        } else if (this.isMultiSelectionMode()) {
            let index: number = this.selection.indexOf(row);
            if (index !== -1) {
                unselectedRow = this.selection.splice(index, 1);
            } else {
                this.selection.push(row);
                selectedRow = row;
            }
        }
        this.rowSelection.emit({selected: selectedRow, unselected: unselectedRow});
    }

    private edit(event: any, column: Column, row: any): void {
        let value: any = row;
        let paths: Array<string> = column.content.split(".");
        while (paths.length > 0) {
            let path: string = paths.shift();
            if (paths.length !== 0) {
                value = value[path];
            } else {
                this.rowEdit.emit({
                    editedRow: row,
                    editedField: column.content,
                    oldValue: value[path],
                    newValue: event
                });
                value[path] = event;
            }
        }
    }

    private handleLazyLoading(event: LazyLoadEvent): void {
        this.onLazy.emit(event);
    }

    private isSingleSelectionMode(): boolean {
        return this.selectionMode === "single" || this.selectionMode === "dblclick";
    }

    private isMultiSelectionMode(): boolean {
        return this.selectionMode === "multi";
    }

    private isDoubleClickSelectionMode(): boolean {
        return this.selectionMode === "dblclick";
    }

    private isSelectionMode(): boolean {
        return this.isOneClickSelectionMode() || this.isDoubleClickSelectionMode();
    }

    private isOneClickSelectionMode(): boolean {
        return this.selectionMode === "multi" || this.selectionMode === "single";
    }

    private getTableClass(): string {
        let tableClasses: string = "table " + this.tableClasses;

        if (this.isSelectionMode()) {
            tableClasses += " table-hover";
        }
        return tableClasses;
    }

    private getSortClass(column: Column): string {
        return "glyphicon glyphicon-" + ((column === this.currentSortedColumn) ?
            column.order === 1 ? "sort-by-attributes-alt" : "sort-by-attributes" : "sort");
    }

    private isSelected(row: any): boolean {
        return this.isOneClickSelectionMode() ? this.selection.indexOf(row) !== -1 : false;
    }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
    selector: "a2-data-table",
    templateUrl: "src/bootstrap/data-table/data-table.component.html"
}, ] },
];
/** @nocollapse */
static ctorParameters: ({type: any, decorators?: DecoratorInvocation[]}|null)[] = [
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'pageSize': [{ type: Input },],
'lazy': [{ type: Input },],
'data': [{ type: Input },],
'selectionMode': [{ type: Input },],
'selection': [{ type: Input },],
'availablePageSizes': [{ type: Input },],
'tableClasses': [{ type: Input },],
'onLazy': [{ type: Output },],
'rowSelection': [{ type: Output },],
'rowEdit': [{ type: Output },],
'columns': [{ type: ContentChildren, args: [forwardRef(() => Column), ] },],
'header': [{ type: ContentChild, args: [forwardRef(() => Header), ] },],
'footer': [{ type: ContentChild, args: [forwardRef(() => Footer), ] },],
'paginator': [{ type: ViewChild, args: ["paginator", ] },],
};
}



export class Column {
     header: string;
     content: string;
     sortBy: string;
     filterBy: string;
     editable: boolean;

    order: number = 1;
static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{
    selector: "a2-column",
}, ] },
];
/** @nocollapse */
static ctorParameters: ({type: any, decorators?: DecoratorInvocation[]}|null)[] = [
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'header': [{ type: Input },],
'content': [{ type: Input },],
'sortBy': [{ type: Input },],
'filterBy': [{ type: Input },],
'editable': [{ type: Input },],
};
}

export interface SelectionEvent {
    selected?: any;
    unselected?: any;
}

export interface EditEvent {
    editedRow: any;
    editedField: string;
    oldValue: any;
    newValue: any;
}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
